import React, { useState, useRef } from 'react';
import { 
  IconButton, 
  TooltipHost, 
  DirectionalHint,
  ContextualMenu,
  IContextualMenuItem,
  IIconProps
} from '@fluentui/react';
import { ChatMessage } from '../../api/models';
import { 
  copyToClipboard, 
  formatMessageForCopy, 
  getAssistantContentForCopy,
  getFormattedMessageContent,
  exportMessage,
  showCopySuccess 
} from '../../utils/conversationUtils';
import styles from './MessageActions.module.css';

interface MessageActionsProps {
  message: ChatMessage;
  conversationTitle?: string;
  className?: string;
}

const copyIcon: IIconProps = { iconName: 'Copy' };
const exportIcon: IIconProps = { iconName: 'Download' };
const moreIcon: IIconProps = { iconName: 'MoreVertical' };

export const MessageActions: React.FC<MessageActionsProps> = ({ 
  message, 
  conversationTitle,
  className 
}) => {
  // Only show actions for assistant messages
  if (message.role !== 'assistant') {
    return null;
  }

  const [showMenu, setShowMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const moreButtonRef = useRef<HTMLElement>(null);

  const handleCopyFull = async () => {
    const content = formatMessageForCopy(message);
    await copyToClipboard(
      content,
      () => {
        setCopySuccess('Full message copied!');
        setTimeout(() => setCopySuccess(null), 2000);
      },
      (error) => console.error('Copy failed:', error)
    );
  };

  const handleCopyContent = async () => {
    let content: string;
    if (message.role === 'assistant') {
      content = getAssistantContentForCopy(message);
    } else {
      content = getFormattedMessageContent(message);
    }
    
    await copyToClipboard(
      content,
      () => {
        setCopySuccess('Content copied!');
        setTimeout(() => setCopySuccess(null), 2000);
      },
      (error) => console.error('Copy failed:', error)
    );
  };

  const handleExport = async (format: 'pdf' | 'docx') => {
    setIsExporting(true);
    await exportMessage(
      message,
      format,
      conversationTitle,
      () => {
        setCopySuccess(`Exported as ${format.toUpperCase()}!`);
        setTimeout(() => setCopySuccess(null), 3000);
      },
      (error) => {
        setCopySuccess(`Export failed: ${error}`);
        setTimeout(() => setCopySuccess(null), 3000);
      }
    );
    setIsExporting(false);
    setShowMenu(false);
  };

  const menuItems: IContextualMenuItem[] = [
    {
      key: 'copyContent',
      text: message.role === 'assistant' ? 'Copy Response' : 'Copy Message',
      iconProps: copyIcon,
      onClick: () => {
        handleCopyContent();
      },
    },
    {
      key: 'copyFull',
      text: 'Copy with Timestamp',
      iconProps: copyIcon,
      onClick: () => {
        handleCopyFull();
      },
    },
    {
      key: 'divider1',
      text: '-',
    },
    {
      key: 'exportPdf',
      text: 'Export as PDF',
      iconProps: exportIcon,
      disabled: isExporting,
      onClick: () => {
        handleExport('pdf');
      },
    },
    {
      key: 'exportDocx',
      text: 'Export as Word',
      iconProps: exportIcon,
      disabled: isExporting,
      onClick: () => {
        handleExport('docx');
      },
    },
  ];

  return (
    <div className={`${styles.messageActions} ${className || ''}`}>
      {copySuccess && (
        <div className={styles.successMessage}>
          {copySuccess}
        </div>
      )}
      
      {/* Quick copy button */}
      <TooltipHost 
        content={message.role === 'assistant' ? 'Copy response' : 'Copy message'}
        directionalHint={DirectionalHint.topCenter}
      >
        <IconButton
          iconProps={copyIcon}
          className={styles.actionButton}
          onClick={handleCopyContent}
          ariaLabel="Copy message content"
        />
      </TooltipHost>

      {/* More options button */}
      <TooltipHost 
        content="More options"
        directionalHint={DirectionalHint.topCenter}
      >
        <IconButton
          iconProps={moreIcon}
          className={styles.actionButton}
          onClick={() => setShowMenu(true)}
          ariaLabel="More message options"
          elementRef={moreButtonRef}
        />
      </TooltipHost>

      {showMenu && (
        <ContextualMenu
          items={menuItems}
          target={moreButtonRef.current}
          onDismiss={() => setShowMenu(false)}
          directionalHint={DirectionalHint.topLeftEdge}
        />
      )}
    </div>
  );
};